import Vue from 'vue'
import store from 'Store'
import ApiActions from 'ServicesActions'
import { checkApiResponse } from 'Utils'

// preparing draft update & sync status
const updateTemplateDraft = (actionType) => {
  if (actionType === 'templates') {
    const today = new Date()
    store.dispatch('templates/updateTemplateDraft', { modified: today })
  }
}
// error status
const commitTemplateDraftSyncStatus = (actionType) => {
  if (actionType === 'templates') {
    store.dispatch('templates/changeSyncStatus', 'error')
  }
}

export const namespaced = true

export const state = {
  checklists: [],
  language: 'es',
}

export const mutations = {
  // Añade un checklist
  SET_CHECKLIST: (state, checklist) => {
    state.checklists.push(checklist)
  },

  // Carga del state con checklist de la tarjeta acutal
  SET_CHECKLISTS: (state, { language, checklists }) => {
    Vue.set(state, 'checklists', checklists)
    Vue.set(state, 'language', language)
  },

  // Actualiza valores de un checklist
  SET_CHECKLIST_VALUE: (state, data) => {
    for (const key of data.keys) {
      data.checklist[key] = data.newData[key]
    }
  },

  SET_CHECKLISTS_LANGUAGE: (state, language) => {
    state.language = language
  },

  // Elimina checklist
  REMOVE_CHECKLIST: (state, checklistId) => {
    state.checklists = state.checklists.filter((checklist) => checklist.id !== checklistId)
  },

  // Añade un item al checklist
  SET_NEW_ITEM: (state, data) => {
    data.checklist.items.push(data.item)
  },

  // Actualiza un item
  SET_ITEM_VALUE: (state, data) => {
    for (const key of data.keys) {
      data.item[key] = data.newData[key]
    }
  },

  // Elimina un item
  REMOVE_CHECKLIST_ITEM: (state, { checklist, itemId }) => {
    const freshItems = checklist.items.filter((item) => item.id !== itemId)
    Vue.set(checklist, 'items', freshItems)
  },
}

export const actions = {
  // Setup -> Carga de checklists al abrir una tarjeta
  load(context, checklists) {
    const language = context.rootState.user.currentUser.language || 'es'
    context.commit('SET_CHECKLISTS', { language, checklists })
  },

  // Setup -> añadir checklist
  async addChecklist(context, { cardId, actionType, checklistValues }) {
    const apiCall = await ApiActions[actionType].checklists.create(cardId, checklistValues)

    if (!checkApiResponse(apiCall)) {
      commitTemplateDraftSyncStatus(actionType)
      return apiCall
    }
    // updating modified template date
    updateTemplateDraft(actionType)
    // updating state
    const newChecklist = {
      ...apiCall.data,
      items: [],
    }

    context.commit('SET_CHECKLIST', newChecklist)
  },

  // Setup -> actualizar checklist
  async updateChecklist(context, { actionType, cardId, checklistId, newData }) {
    const apiCall = await ApiActions[actionType].checklists.update(cardId, checklistId, newData)

    if (!checkApiResponse(apiCall)) {
      commitTemplateDraftSyncStatus(actionType)
      return apiCall
    }
    // updating modified template date
    updateTemplateDraft(actionType)
    // updating state
    const toUpdate = Object.keys(newData)
    const updateData = {
      checklist: context.getters.getChecklist(checklistId),
      keys: toUpdate,
      newData,
    }
    context.commit('SET_CHECKLIST_VALUE', updateData)
    return apiCall
  },

  // Setup -> eliminar checklist
  async deleteChecklist({ commit }, { actionType, cardId, checklistId }) {
    const apiCall = await ApiActions[actionType].checklists.delete(cardId, checklistId)
    // if delete error, we'll recieve a response
    if (!checkApiResponse(apiCall)) {
      // console.log(actionType)
      commitTemplateDraftSyncStatus(actionType)
      return apiCall
    }

    // updating modified template date
    updateTemplateDraft(actionType)
    // updating state
    commit('REMOVE_CHECKLIST', checklistId)
  },

  // Setup -> Añadir item a checklist
  async addItem(context, { actionType, cardId, checklistId, itemValues }) {
    const apiCall = await ApiActions[actionType].checklists.items.create(
      cardId,
      checklistId,
      itemValues
    )

    if (!checkApiResponse(apiCall)) {
      commitTemplateDraftSyncStatus(actionType)
      return apiCall
    }

    // updating modified template date
    updateTemplateDraft(actionType)
    // updating state
    const newItem = {
      checklist: context.getters.getChecklist(checklistId),
      item: {
        checklist: checklistId,
        ...apiCall.data,
      },
    }
    context.commit('SET_NEW_ITEM', newItem)
  },

  // Setup -> Actualizar item de checklist
  async updateItem(context, { actionType, cardId, checklistId, itemId, newData }) {
    const apiCall = await ApiActions[actionType].checklists.items.update({
      cardId,
      checklistId,
      itemId,
      newData,
    })
    if (!checkApiResponse(apiCall)) {
      commitTemplateDraftSyncStatus(actionType)
      return apiCall
    }
    // updating modified template date
    updateTemplateDraft(actionType)
    // updating state
    const toUpdate = Object.keys(newData)
    const updateData = {
      item: context.getters.getChecklistItem(checklistId, itemId),
      keys: toUpdate,
      newData,
    }
    context.commit('SET_ITEM_VALUE', updateData)
    return apiCall
  },

  // Setup -> Eliminar Item
  async deleteItem(context, { actionType, cardId, checklistId, itemId }) {
    const apiCall = await ApiActions[actionType].checklists.items.delete(
      cardId,
      checklistId,
      itemId
    )
    // if delete error, we'll recieve a response
    if (checkApiResponse(apiCall)) {
      commitTemplateDraftSyncStatus(actionType)
      return apiCall
    }

    // updating modified template date
    updateTemplateDraft(actionType)
    // updating state
    const checklist = context.getters.getChecklist(checklistId)
    context.commit('REMOVE_CHECKLIST_ITEM', { checklist, itemId })
  },
  // Change language
  changeLanguage({ commit }, language) {
    commit('SET_CHECKLISTS_LANGUAGE', language)
  },
}

export const getters = {
  getChecklist: (state) => (id) => {
    return state.checklists.filter((checklist) => checklist.id == id)[0]
  },

  isChecklistCompleted: (state, getters) => (id) => {
    return [getters.getChecklist(id)].reduce((acc, actual) => {
      if (actual.items.length > 0 && !actual.items.some((item) => item.is_completed === false)) {
        acc = true
      }
      return acc
    }, false)
  },

  getChecklistItems: (state) => (checklistId) => {
    return state.checklists.filter((checklist) => checklist.id === checklistId)[0].items
  },

  getChecklistItem: (state) => (checklistId, itemId) => {
    return state.checklists
      .filter((checklist) => checklist.id === checklistId)[0]
      .items.filter((item) => item.id == itemId)[0]
  },

  getItemChecklist: (state) => (itemId) => {
    return state.checklists.reduce((acc, actual) => {
      if (actual.items.some((item) => item.id === itemId)) {
        acc = actual
      }
      return acc
    }, {})
  },
}
